import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Database,
  FileSpreadsheet,
  Globe,
  Server,
  ArrowRight,
  Zap,
  BarChart3,
  Table,
  Cloud,
  Layers,
  Code,
  CheckCircle2,
} from 'lucide-react';

const iconMap = {
  database: Database,
  spreadsheet: FileSpreadsheet,
  globe: Globe,
  server: Server,
  zap: Zap,
  chart: BarChart3,
  table: Table,
  cloud: Cloud,
  layers: Layers,
  code: Code,
  check: CheckCircle2,
};

const colorMap = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    glow: 'shadow-blue-200/60',
    icon: 'text-blue-500',
    dot: 'bg-blue-500',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    glow: 'shadow-green-200/60',
    icon: 'text-green-500',
    dot: 'bg-green-500',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    glow: 'shadow-purple-200/60',
    icon: 'text-purple-500',
    dot: 'bg-purple-500',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    glow: 'shadow-amber-200/60',
    icon: 'text-amber-500',
    dot: 'bg-amber-500',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    glow: 'shadow-red-200/60',
    icon: 'text-red-500',
    dot: 'bg-red-500',
  },
  gray: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
    glow: 'shadow-gray-200/60',
    icon: 'text-gray-500',
    dot: 'bg-gray-500',
  },
};

function StepBox({ step, isActive, onClick, index }) {
  const Icon = iconMap[step.icon] || Layers;
  const colors = colorMap[step.color] || colorMap.gray;

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      onClick={() => onClick?.(step.id)}
      className={`relative flex flex-col items-center gap-1.5 rounded-xl border-2 px-3 sm:px-4 py-3 min-w-[120px] max-w-[190px] transition-all duration-200 cursor-pointer ${
        colors.bg
      } ${colors.border} ${
        isActive ? `shadow-lg ${colors.glow} scale-105` : 'shadow-sm hover:shadow-md'
      }`}
    >
      {isActive && (
        <motion.div
          className={`absolute inset-0 rounded-xl border-2 ${colors.border} opacity-50`}
          animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
        />
      )}
      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.icon} shrink-0`} />
      <span className={`text-xs sm:text-sm font-semibold ${colors.text} text-center leading-snug break-words w-full`}>
        {step.title || step.label || step.name || 'Step'}
      </span>
      {(step.description || step.sublabel) && (
        <span className="text-[11px] text-slate-500 text-center leading-snug break-words w-full">
          {step.description || step.sublabel}
        </span>
      )}
    </motion.button>
  );
}

function Arrow({ connection, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 + 0.05, duration: 0.2 }}
      className="flex flex-col items-center justify-center px-1 shrink-0"
    >
      <div className="relative flex items-center">
        <div className="w-6 sm:w-10 h-0.5 bg-gray-300 relative overflow-hidden">
          {connection.animated && (
            <motion.div
              className="absolute top-0 left-0 w-2 h-full bg-blue-400 rounded-full"
              animate={{ x: ['-8px', '48px'] }}
              transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
            />
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-gray-400 -ml-1" />
      </div>
      {connection.label && (
        <span className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
          {connection.label}
        </span>
      )}
    </motion.div>
  );
}

export default function DataFlowDiagram({
  steps = [],
  connections = [],
  activeStep = null,
  onStepClick,
}) {
  // Build ordered list: step, arrow, step, arrow, step...
  // For simplicity, render connections between consecutive steps based on the connections array
  const stepOrder = steps.map((s) => s.id);

  // Build a map from -> connection
  const connMap = {};
  for (const conn of connections) {
    connMap[conn.from] = conn;
  }

  return (
    <div className="w-full overflow-x-auto py-4">
      <div className="flex items-center justify-start gap-0 min-w-max px-2">
        {steps.map((step, idx) => {
          const conn = connMap[step.id];
          return (
            <div key={step.id} className="flex items-center">
              <StepBox
                step={step}
                isActive={activeStep === step.id}
                onClick={onStepClick}
                index={idx}
              />
              {conn && idx < steps.length - 1 && (
                <Arrow connection={conn} index={idx} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
